import { isJSONString } from './JSONUtils'
import { IsVarEmpty, isVarNotEmpty } from './GlobalFunctions'
import { LocalStorageItem, LocalStorageSetterOptions } from './StorageUtils'

export class $SessionStorage {
    private static storage: Storage = sessionStorage

    private static $getItem<T>(key: string, version?: string): Nullable<T> {
        const obj: Nullable<string> = $SessionStorage.storage.getItem(key)

        if (!obj || !isJSONString(obj)) return null

        const parsed: T = JSON.parse(obj) as T

        if (typeof parsed !== 'object') return parsed

        const lsItem: LocalStorageItem<unknown> = parsed as Record<string, unknown> as LocalStorageItem<unknown>

        if (!IsVarEmpty(lsItem?.version) && !IsVarEmpty(version)) {
            if (
                !~lsItem?.version?.localeCompare(version, undefined, {
                    numeric: true,
                    sensitivity: 'base'
                })
            ) {
                return null
            }
        }

        if (!IsVarEmpty(lsItem?.expiry) && !IsVarEmpty(lsItem?.value)) {
            const { expiry, value }: LocalStorageItem<unknown> = lsItem
            const now: number = new Date().getTime()

            if (now > expiry) {
                $SessionStorage.storage.removeItem(key)
                return null
            }

            return value as T
        }

        return parsed
    }

    private static $setItem(key: string, value: unknown, options?: LocalStorageSetterOptions): void {
        const ttl: Nullable<number> = options?.daysToLive ? options.daysToLive * 86400000 : null
        const now: number = new Date().getTime()
        const item: unknown =
            IsVarEmpty(ttl) && IsVarEmpty(options?.version)
                ? value
                : {
                      value,
                      version: options?.version,
                      expiry: ttl ? now + ttl : undefined
                  }

        $SessionStorage.storage.setItem(key, JSON.stringify(item))
    }

    public static getItem<T>(key: string, version?: string): Nullable<T> {
        return $SessionStorage.$getItem<T>(key, version)
    }

    public static setItem(key: string, value: unknown, options?: LocalStorageSetterOptions): void {
        return $SessionStorage.$setItem(key, value, options)
    }

    public static hasItem(key: string): boolean {
        return isVarNotEmpty($SessionStorage.storage.getItem(key))
    }

    public static removeItem(key: string): void {
        $SessionStorage.storage.removeItem(key)
    }

    public static clear(): void {
        $SessionStorage.storage.clear()
    }
}
